import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { SAR } from "../components/RiyalIcon";
import { Car, Store, Bike, ArrowLeft, Minus, Plus } from "lucide-react";
import { DeliveryLocationPicker } from "../components/DeliveryLocationPicker";

export default function DemoCheckout() {
  const nav = useNavigate();
  const cart = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [method, setMethod] = useState("STORE");
  const [carPickupActive, setCarPickupActive] = useState(false);
  const [f, setF] = useState({
    customer_name: "أحمد التجريبي", customer_phone: "0501234567",
    car_type: "كامري", car_color: "أبيض", car_plate: "9999",
    delivery_address: "", delivery_notes: "", notes: "",
    delivery_latitude: null, delivery_longitude: null,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF(s => ({ ...s, [k]: v }));
  const hasDeliveryLocation = Number.isFinite(f.delivery_latitude)
    && Number.isFinite(f.delivery_longitude);
  const pickupMethod = method === "STORE" && carPickupActive ? "CAR" : method;
  const selectMethod = (nextMethod) => {
    setMethod(nextMethod);
    if (nextMethod !== "STORE") {
      setCarPickupActive(false);
    }
  };

  useEffect(() => {
    api.get("/demo/restaurant").then(r => {
      setRestaurant(r.data.restaurant);
      setMethod("STORE");
    });
  }, []);

  const submit = async () => {
    if (!/^05\d{8}$/.test(f.customer_phone)) return toast.error("رقم الجوال غير صحيح");
    if (pickupMethod === "CAR" && (!f.car_type || !f.car_color || !f.car_plate)) return toast.error("أكمل بيانات السيارة");
    if (method === "DELIVERY" && !hasDeliveryLocation) return toast.error("حدد موقع التوصيل من الخريطة");
    if (cart.items.length === 0) return toast.error("السلة فارغة");
    setBusy(true);
    try {
      const payload = {
        restaurant_id: "__demo__",
        items: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_addons: i.selected_addons || [] })),
        pickup_method: pickupMethod,
        payment_method: "ON_PICKUP",
        source: "ONLINE",
        customer_name: f.customer_name,
        customer_phone: f.customer_phone,
        notes: f.notes,
      };
      if (pickupMethod === "CAR") {
        Object.assign(payload, {
          car_type: f.car_type,
          car_color: f.car_color,
          car_plate: f.car_plate,
        });
      }
      if (method === "DELIVERY") {
        Object.assign(payload, {
          delivery_address: f.delivery_address,
          delivery_notes: f.delivery_notes,
          delivery_latitude: f.delivery_latitude,
          delivery_longitude: f.delivery_longitude,
        });
      }
      const { data } = await api.post("/demo/orders", payload);
      cart.clear();
      nav(`/demo/order/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const OPTIONS = [
    { key: "STORE", icon: Store, label: "استلام من المحل" },
    ...(restaurant?.delivery_enabled
      ? [{ key: "DELIVERY", icon: Bike, label: "توصيل إلى موقعك" }]
      : []),
  ];
  const cols = OPTIONS.length === 1 ? "grid-cols-1" : "grid-cols-2";

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-32">
      <header className="max-w-md mx-auto flex items-center gap-3 px-5 py-4">
        <button onClick={() => nav("/demo/customer")} className="w-10 h-10 rounded-full bg-white grid place-items-center" data-testid="back-btn"><ArrowLeft size={18} className="rotate-180"/></button>
        <h1 className="text-2xl font-extrabold">إتمام الطلب</h1>
      </header>

      <div className="max-w-md mx-auto px-5 space-y-5">
        {cart.items.length > 0 && (
          <div className="bg-white rounded-2xl border border-orange-100 p-3 space-y-1.5 text-sm">
            {cart.items.map(i => (
              <div key={i._key}>
                <div className="flex justify-between">
                  <span>{i.quantity} × {i.name}</span>
                  <span className="num-badge text-gray-500">
                    <SAR>{cart.lineTotal(i)}</SAR>
                  </span>
                </div>
                {(i.addon_snapshot || []).map((addon, index) => (
                  <div
                    key={`${addon.option_id || addon.name}-${index}`}
                    className="text-xs text-gray-500"
                  >
                    + {addon.name}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <section>
          <div className="text-sm font-bold text-gray-700 mb-2">طريقة الاستلام</div>
          <div className={`grid gap-2 ${cols}`}>
            {OPTIONS.map(o => (
              <button key={o.key} onClick={() => selectMethod(o.key)} data-testid={`method-${o.key}`}
                className={`rounded-2xl p-3 border-2 text-center font-bold text-sm ${method === o.key ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white text-gray-700"}`}>
                <o.icon className="mx-auto mb-1" size={22}/>
                <div className="leading-tight text-xs">{o.label}</div>
              </button>
            ))}
          </div>
        </section>

        {method === "STORE" && restaurant?.car_pickup_enabled && (
          <section className="pt-1">
            <button
              type="button"
              onClick={() => setCarPickupActive((active) => !active)}
              data-testid="method-CAR"
              className={
                "inline-flex items-center gap-1 text-xs font-bold transition-colors duration-200 "
                + (carPickupActive
                  ? "text-orange-700"
                  : "text-gray-600")
              }
              aria-pressed={carPickupActive}
              aria-expanded={carPickupActive}
            >
              <Car size={16} />
              استلام عند السيارة
              {carPickupActive ? (
                <Minus size={16} data-testid="car-pickup-minus-icon" />
              ) : (
                <Plus size={16} data-testid="car-pickup-plus-icon" />
              )}
            </button>
            {carPickupActive && (
              <div
                className="mt-2 border-t border-gray-200/70"
                data-testid="car-pickup-divider"
              />
            )}
            <div
              className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
                carPickupActive ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
              data-testid="car-pickup-fields-panel"
              aria-hidden={!carPickupActive}
            >
              <div className="min-h-0">
                <div className="mt-3 space-y-2 pb-3 pr-3">
                  <Field
                    label="نوع السيارة"
                    value={f.car_type}
                    onChange={v => upd("car_type", v)}
                    testid="car-type"
                    compact
                  />
                  <Field
                    label="اللون"
                    value={f.car_color}
                    onChange={v => upd("car_color", v)}
                    testid="car-color"
                    compact
                  />
                  <Field
                    label="آخر 4 أرقام من اللوحة"
                    value={f.car_plate}
                    onChange={v => upd("car_plate", v)}
                    testid="car-plate"
                    compact
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {method === "DELIVERY" && (
          <section className="space-y-2">
            <DeliveryLocationPicker
              latitude={f.delivery_latitude}
              longitude={f.delivery_longitude}
              onLocationChange={({ latitude, longitude }) => {
                setF((state) => ({
                  ...state,
                  delivery_latitude: latitude,
                  delivery_longitude: longitude,
                }));
              }}
              onAddressDetected={(deliveryAddress) => {
                setF((state) => ({
                  ...state,
                  delivery_address: deliveryAddress,
                }));
              }}
            />
            <Field label="ملاحظات الموقع (اختياري)" value={f.delivery_notes} onChange={v => upd("delivery_notes", v)} testid="delivery-notes" />
          </section>
        )}

        <section className="space-y-2">
          <Field label="الاسم (اختياري)" value={f.customer_name} onChange={v => upd("customer_name", v)} testid="customer-name" />
          <Field label="رقم الجوال" value={f.customer_phone} onChange={v => upd("customer_phone", v)} testid="customer-phone" />
        </section>

        <section className="bg-white border border-orange-100 rounded-2xl p-4">
          <div className="text-sm font-bold text-gray-700 mb-1">طريقة الدفع</div>
          <div className="flex items-center gap-2 text-orange-700 font-bold">💵 الدفع عند الاستلام</div>
        </section>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30">
        <div className="max-w-md mx-auto p-4">
          <div className="bg-white rounded-2xl card-shadow border border-orange-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600">الإجمالي</span>
              <span className="text-2xl font-extrabold text-orange-600"><SAR>{cart.total + (method === "DELIVERY" ? 10 : 0)}</SAR></span>
            </div>
            <button disabled={busy} onClick={submit} className="w-full pill brand-grad text-white font-bold text-base py-3.5 tap-target disabled:opacity-60" data-testid="confirm-order">
              {busy ? "جاري التأكيد…" : "تأكيد الطلب التجريبي"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
function Field({ label, value, onChange, testid, compact = false }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className={
          `mt-1 w-full ${compact ? "h-[42px]" : "h-12"} rounded-2xl bg-white border `
          + "border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 "
          + "focus:ring-orange-100"
        }
      />
    </label>
  );
}
